/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import routeConfiguration, {
  ACCOUNT_SETTINGS_PAGES,
  draftId,
  draftSlug,
} from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  Icons,
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import Drawer from 'react-modern-drawer';
import './Drawer.css';
import { getUserType } from '../../../../util/userDataExtraction';
import { topbarNavSubMenu, UserType } from '../../../../util/enums';
import { EditListingMenuLink } from '../TopbarDesktop/CustomLinksMenu/PriorityLinks';
import IconArrowRight from '../../../../components/IconArrowRight/IconArrowRight';
import IconSocialMediaTiktok from '../../../../components/IconSocialMediaTiktok/IconSocialMediaTiktok';
import { createResourceLocatorString } from '../../../../util/routes';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import CustomLinksMenu from '../TopbarDesktop/CustomLinksMenu/CustomLinksMenu';
import { createSlug } from '../../../../util/urlHelpers';
import { EDIT } from '../../../../util/constants';
import { DETAILS } from '../../../EditListingPage/EditListingWizard/EditListingWizardTab';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    intl,
    customLinks,
    onLogout,
    currentUserListing,
  } = props;

  const history = useHistory();

  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState({ index: undefined, status: false });
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const user = ensureCurrentUser(currentUser);
  const userType = getUserType(currentUser);
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const extraLinks = customLinks.map(linkConfig => {
    return (
      <CustomLinkComponent
        key={linkConfig.text}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const drawerData = (
    <>
      <div className={css.browseMenu}>
        <h2>More Links</h2>
        <div className={css.links}>
          <div className={css.linkItem}>
            <NamedLink name="CMSPage" params={{ pageId: 'member-pathway' }}>
              <FormattedMessage id="TopbarDesktop.thePathway" /> <IconArrowRight />
            </NamedLink>
          </div>
          <div className={css.linkItem}>
            <NamedLink name="CMSPage" params={{ pageId: 'the-edge-zone-system' }}>
              <FormattedMessage id="TopbarDesktop.edgeZone" /> <IconArrowRight />
            </NamedLink>
          </div>

          {topbarNavSubMenu.map((menu, index) => (
            <div
              key={index}
              className={css.linkItem}
              onClick={() => setIsOpen({ index: index, status: !isOpen.status })}
            >
              <span className={css.navLink}>
                <FormattedMessage id={menu.label} />
                {menu.subMenu && <IconArrowRight />}
              </span>
              <Drawer
                key={index}
                open={isOpen.status && isOpen.index === index}
                onClose={() => setIsOpen(!isOpen)}
                direction="left"
              >
                <div className={classNames(css.browseMenu, css.drawerMenu)}>
                  <div className={css.drawerHead}>
                    <span className={css.back} onClick={() => setIsOpen(!isOpen)}>
                      <IconArrowRight />
                    </span>
                    <span className={css.cross} onClick={() => setIsOpen(!isOpen)}>
                      &times;
                    </span>
                  </div>

                  {menu.subMenu && (
                    <div className={css.links}>
                      {menu.subMenu.map((submenu, subIndex) => (
                        <div key={subIndex} className={css.linkItem}>
                          {submenu.subSubMenu ? (
                            <span
                              onClick={() =>
                                setOpenSubmenu(openSubmenu === subIndex ? null : subIndex)
                              }
                            >
                              <FormattedMessage id={submenu.label} />
                              <Icons name="rightAngle" />
                            </span>
                          ) : submenu.type === 'external' ? (
                            <ExternalLink href={submenu.href}>
                              <FormattedMessage id={submenu.label} /> <IconArrowRight />
                            </ExternalLink>
                          ) : (
                            <NamedLink name={submenu.href} params={submenu?.params}>
                              <FormattedMessage id={submenu.label} /> <IconArrowRight />
                            </NamedLink>
                          )}

                          {openSubmenu === subIndex && submenu.subSubMenu && (
                            <div className={css.links}>
                              {submenu.subSubMenu.map((subSubItem, subSubIndex) => (
                                <div key={subSubIndex} className={css.linkItem}>
                                  {subSubItem.type === 'external' ? (
                                    <ExternalLink href={subSubItem.href}>
                                      <FormattedMessage id={subSubItem.label} />
                                      <IconArrowRight />
                                    </ExternalLink>
                                  ) : (
                                    <NamedLink name={subSubItem.href} params={submenu?.params}>
                                      <FormattedMessage id={subSubItem.label} /> <IconArrowRight />
                                    </NamedLink>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Drawer>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    return (
      <div className={css.root}>
        <div className={css.content}>
          {login}
          {signup}
          {drawerData}
          <div className={css.customLinksWrapper}>{extraLinks}</div>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    const isMessagesPage =
      page === 'MessagesPage' &&
      (currentPage === 'MessagesPage' || currentPage === 'MessagesBasePage');
    return currentPage === page || isAccountSettingsPage || isInboxPage || isMessagesPage
      ? css.currentPage
      : null;
  };
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';

  const onRedirectPage = (page, pageParams = {}) => {
    history.push(createResourceLocatorString(page, routeConfiguration(), pageParams, {}));
  };

  const currentListingId = currentUserListing?.id?.uuid;
  const currentListingTitle = currentUserListing?.attributes?.title;

  return (
    <div className={css.root}>
      <div
        className={css.userAccount}
        role="button"
        onClick={() =>
          onRedirectPage('ListingPage', {
            id: currentListingId || draftId,
            slug: createSlug(currentListingTitle || draftSlug),
          })
        }
      >
        <AvatarLarge className={css.avatar} user={currentUser} disableProfileLink />
      </div>

      <div className={css.content}>
        <div className={css.greetingSec}>
          <span className={css.greeting}>
            <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
          </span>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </div>

        <div className={css.links}>
          <NamedLink className={css.linkItem} name="LandingPage">
            <FormattedMessage id="TopbarDesktop.home" />
          </NamedLink>
          <NamedLink
            className={classNames(css.linkItem, currentPageClass('MessagesPage'))}
            name="MessagesBasePage"
          >
            <FormattedMessage id="TopbarMobileMenu.messagesLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.linkItem, currentPageClass(`InboxPage:${inboxTab}`))}
            name="OrdersListViewPage"
            // params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          <CustomLinksMenu
            currentPage={currentPage}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
            customClassName={css.linkItem}
            currentUserListing={currentUserListing}
            userType={userType}
            isTopbarMobileMenuLink={true}
          />
          <NamedLink
            className={classNames(css.linkItem, currentPageClass('SubscriptionPage'))}
            name="SubscriptionPage"
          >
            <FormattedMessage id="TopbarDesktop.SubscriptionPage" />
          </NamedLink>
          {/* <NamedLink
            className={classNames(css.linkItem, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink> */}
          {/* <NamedLink
            className={classNames(css.linkItem, currentPageClass('FavoritesPage'))}
            name="FavoritesPage"
          >
            <FormattedMessage id="TopbarMobileMenu.FavoritesPageLink" />
          </NamedLink> */}
          {userType !== UserType.TALENT && (
            <NamedLink
              className={classNames(css.linkItem, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink>
          )}
          <div className={css.customLinksWrapper}>{extraLinks}</div>
        </div>
      </div>
      {drawerData}
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
